import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';
import type {
  AuditLog,
  CalculatorResult,
  CreateInvestorResponse,
  Dashboard,
  CreateDealerResponse,
  Dealer,
  DealerAccountDashboard,
  DealerCredentials,
  DealerDashboard,
  DealerDashboardSummary,
  DealerInvestment,
  DealerLedger,
  DealerListItem,
  DealerPlacement,
  DealerPrincipalReturn,
  DealerProfitReceipt,
  DocumentType,
  Investment,
  Investor,
  InvestorCredentials,
  InvestorDashboard,
  InvestorDocuments,
  InvestorListItem,
  Ledger,
  PagedResult,
  ProfitPayment,
  ProfitResult,
  User,
  Withdrawal,
} from '../types';

export const queryKeys = {
  dashboard: ['dashboard'] as const,
  investors: (params: unknown) => ['investors', params] as const,
  investor: (id: number) => ['investor', id] as const,
  investments: (id: number) => ['investments', id] as const,
  withdrawals: (id: number) => ['withdrawals', id] as const,
  profit: (id: number, from?: string, to?: string) => ['profit', id, from, to] as const,
  payments: (id: number) => ['payments', id] as const,
  ledger: (id: number) => ['ledger', id] as const,
  users: ['users'] as const,
  auditLogs: (params: unknown) => ['audit-logs', params] as const,
  dealerDashboard: ['dealer-dashboard'] as const,
  dealerInvestments: (status?: string) => ['dealer-investments', status] as const,
};

const get = async <T,>(url: string, params?: unknown): Promise<T> =>
  (await api.get<T>(url, { params: params as Record<string, unknown> })).data;

// ---------- dashboard ----------

export const useDashboard = () =>
  useQuery({ queryKey: queryKeys.dashboard, queryFn: () => get<Dashboard>('/dashboard') });

/** Self-service KPIs for one investor. The API rejects a mismatched id, so this is safe to call with the token's own investorId. */
export const useInvestorDashboard = (investorId: number) =>
  useQuery({
    queryKey: ['investor-dashboard', investorId],
    queryFn: () => get<InvestorDashboard>(`/investors/${investorId}/dashboard`),
    enabled: investorId > 0,
  });

// ---------- investors ----------

export interface InvestorQuery {
  page: number;
  pageSize: number;
  search?: string;
  status?: string;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
}

export const useInvestors = (params: InvestorQuery) =>
  useQuery({
    queryKey: queryKeys.investors(params),
    queryFn: () => get<PagedResult<InvestorListItem>>('/investors', params),
    placeholderData: (previous) => previous,
  });

export const useInvestor = (id: number) =>
  useQuery({
    queryKey: queryKeys.investor(id),
    queryFn: () => get<Investor>(`/investors/${id}`),
    enabled: id > 0,
  });

export const useCreateInvestor = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: Record<string, unknown>) =>
      (await api.post<CreateInvestorResponse>('/investors', body)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['investors'] }),
  });
};

export const useUpdateInvestor = (id: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: Record<string, unknown>) =>
      (await api.put<Investor>(`/investors/${id}`, body)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.investor(id) });
      qc.invalidateQueries({ queryKey: ['investors'] });
    },
  });
};

/**
 * Re-issues the investor's portal password. Nothing is cached: the password exists only in this
 * response, so keeping it in the query cache would leave it sitting in memory long after it was
 * read off the screen.
 */
export const useResetInvestorPassword = (id: number) =>
  useMutation({
    mutationFn: async () =>
      (await api.post<InvestorCredentials>(`/investors/${id}/reset-password`)).data,
  });

export const useUpdateInvestorStatus = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, status }: { id: number; status: string }) =>
      api.patch(`/investors/${id}/status`, { status }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['investors'] }),
  });
};

// ---------- documents ----------

export const useUploadDocument = (investorId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ type, file }: { type: DocumentType; file: File }) => {
      const form = new FormData();
      form.append('file', file);
      // Let the browser set the multipart boundary — overriding Content-Type here breaks the upload.
      const { data } = await api.post<InvestorDocuments>(
        `/investors/${investorId}/documents/${type}`,
        form,
        { headers: { 'Content-Type': undefined } },
      );
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.investor(investorId) }),
  });
};

export const useDeleteDocument = (investorId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (type: DocumentType) =>
      (await api.delete<InvestorDocuments>(`/investors/${investorId}/documents/${type}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.investor(investorId) }),
  });
};

/**
 * Documents are behind JWT auth, so they cannot be used directly as an <img src>.
 * This fetches the bytes and returns an object URL the caller must revoke when done.
 */
export async function fetchDocumentObjectUrl(investorId: number, type: DocumentType): Promise<string> {
  const response = await api.get(`/investors/${investorId}/documents/${type}`, { responseType: 'blob' });
  return URL.createObjectURL(response.data as Blob);
}

// ---------- investments ----------

export const useInvestments = (id: number) =>
  useQuery({
    queryKey: queryKeys.investments(id),
    queryFn: () => get<Investment[]>(`/investors/${id}/investments`),
    enabled: id > 0,
  });

/**
 * Sent as multipart so the proof-of-payment image travels with the investment itself — a separate
 * upload call could fail and leave a recorded investment with no proof.
 */
export const useAddInvestment = (id: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      amount: number;
      profitRate: number;
      investmentDate: string;
      notes?: string;
      transactionImage?: File | null;
    }) => {
      const form = new FormData();
      form.append('amount', String(input.amount));
      form.append('profitRate', String(input.profitRate));
      form.append('investmentDate', input.investmentDate);
      if (input.notes) form.append('notes', input.notes);
      if (input.transactionImage) form.append('transactionImage', input.transactionImage);

      // Let the browser set the multipart boundary — overriding Content-Type breaks the upload.
      const { data } = await api.post<Investment>(`/investors/${id}/investments`, form, {
        headers: { 'Content-Type': undefined },
      });
      return data;
    },
    onSuccess: () => invalidateInvestorData(qc, id),
  });
};

/** Fetches a tranche's transaction image as an object URL; caller must revoke it. */
export async function fetchTransactionImageUrl(investorId: number, investmentId: number): Promise<string> {
  const response = await api.get(
    `/investors/${investorId}/investments/${investmentId}/transaction-image`,
    { responseType: 'blob' },
  );
  return URL.createObjectURL(response.data as Blob);
}

// ---------- withdrawals ----------

export const useWithdrawals = (id: number) =>
  useQuery({
    queryKey: queryKeys.withdrawals(id),
    queryFn: () => get<Withdrawal[]>(`/investors/${id}/withdrawals`),
    enabled: id > 0,
  });

/**
 * Sent as multipart so the proof-of-transaction image travels with the withdrawal itself — a
 * separate upload call could fail and leave a recorded withdrawal with no proof.
 */
export const useAddWithdrawal = (id: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      investmentId: number;
      amount: number;
      withdrawalDate: string;
      reason?: string;
      transactionImage?: File | null;
    }) => {
      const form = new FormData();
      form.append('investmentId', String(input.investmentId));
      form.append('amount', String(input.amount));
      form.append('withdrawalDate', input.withdrawalDate);
      if (input.reason) form.append('reason', input.reason);
      if (input.transactionImage) form.append('transactionImage', input.transactionImage);

      const { data } = await api.post<Withdrawal>(`/investors/${id}/withdrawals`, form, {
        headers: { 'Content-Type': undefined },
      });
      return data;
    },
    onSuccess: () => invalidateInvestorData(qc, id),
  });
};

/** Fetches a withdrawal's transaction image as an object URL; caller must revoke it. */
export async function fetchWithdrawalImageUrl(investorId: number, withdrawalId: number): Promise<string> {
  const response = await api.get(
    `/investors/${investorId}/withdrawals/${withdrawalId}/transaction-image`,
    { responseType: 'blob' },
  );
  return URL.createObjectURL(response.data as Blob);
}

// ---------- profit ----------

export const useProfit = (id: number, from?: string, to?: string) =>
  useQuery({
    queryKey: queryKeys.profit(id, from, to),
    queryFn: () => get<ProfitResult>(`/investors/${id}/profit`, { from, to }),
    enabled: id > 0,
  });

export const useProfitPayments = (id: number) =>
  useQuery({
    queryKey: queryKeys.payments(id),
    queryFn: () => get<ProfitPayment[]>(`/investors/${id}/profit-payments`),
    enabled: id > 0,
  });

/**
 * Sent as multipart so the proof-of-transaction image travels with the payment itself — a separate
 * upload call could fail and leave a recorded payment with no proof.
 */
export const useRecordPayment = (id: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      amount: number;
      paymentDate: string;
      paymentMethod?: string;
      receiptNo?: string;
      notes?: string;
      transactionImage?: File | null;
    }) => {
      const form = new FormData();
      form.append('amount', String(input.amount));
      form.append('paymentDate', input.paymentDate);
      if (input.paymentMethod) form.append('paymentMethod', input.paymentMethod);
      if (input.receiptNo) form.append('receiptNo', input.receiptNo);
      if (input.notes) form.append('notes', input.notes);
      if (input.transactionImage) form.append('transactionImage', input.transactionImage);

      const { data } = await api.post<ProfitPayment>(`/investors/${id}/profit-payments`, form, {
        headers: { 'Content-Type': undefined },
      });
      return data;
    },
    onSuccess: () => invalidateInvestorData(qc, id),
  });
};

/** Fetches a profit payment's transaction image as an object URL; caller must revoke it. */
export async function fetchProfitPaymentImageUrl(investorId: number, paymentId: number): Promise<string> {
  const response = await api.get(
    `/investors/${investorId}/profit-payments/${paymentId}/transaction-image`,
    { responseType: 'blob' },
  );
  return URL.createObjectURL(response.data as Blob);
}

// ---------- ledger ----------

export const useLedger = (id: number) =>
  useQuery({
    queryKey: queryKeys.ledger(id),
    queryFn: () => get<Ledger>(`/investors/${id}/ledger`),
    enabled: id > 0,
  });

// ---------- calculator ----------

export const useCalculator = (principal: number, rate: number, days: number, enabled: boolean) =>
  useQuery({
    queryKey: ['calculator', principal, rate, days],
    queryFn: () => get<CalculatorResult>('/calculator', { principal, rate, days }),
    enabled,
  });

// ---------- users ----------

export const useUsers = () =>
  useQuery({ queryKey: queryKeys.users, queryFn: () => get<User[]>('/users') });

export const useCreateUser = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: Record<string, unknown>) => (await api.post<User>('/users', body)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.users }),
  });
};

export const useUpdateUser = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...body }: { id: number } & Record<string, unknown>) =>
      (await api.put<User>(`/users/${id}`, body)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.users }),
  });
};

// ---------- audit ----------

export interface AuditQuery {
  page: number;
  pageSize: number;
  search?: string;
  entityType?: string;
}

export const useAuditLogs = (params: AuditQuery) =>
  useQuery({
    queryKey: queryKeys.auditLogs(params),
    queryFn: () => get<PagedResult<AuditLog>>('/audit-logs', params),
    placeholderData: (previous) => previous,
  });

/** Any money movement changes profit, ledger and the dashboard, so they all refetch together. */
function invalidateInvestorData(qc: ReturnType<typeof useQueryClient>, id: number) {
  qc.invalidateQueries({ queryKey: queryKeys.investments(id) });
  qc.invalidateQueries({ queryKey: queryKeys.withdrawals(id) });
  qc.invalidateQueries({ queryKey: queryKeys.payments(id) });
  qc.invalidateQueries({ queryKey: queryKeys.ledger(id) });
  qc.invalidateQueries({ queryKey: ['profit', id] });
  qc.invalidateQueries({ queryKey: ['investors'] });
  qc.invalidateQueries({ queryKey: queryKeys.dashboard });
}

// ---------- dealer investments (admin's own outbound placements) ----------

export const useDealerDashboard = () =>
  useQuery({
    queryKey: queryKeys.dealerDashboard,
    queryFn: async () => (await api.get<DealerDashboard>('/dealer-investments/dashboard')).data,
  });

export const useDealerInvestments = (status?: string) =>
  useQuery({
    queryKey: queryKeys.dealerInvestments(status),
    queryFn: async () =>
      (await api.get<DealerInvestment[]>('/dealer-investments', { params: { status } })).data,
  });

/** Both the list and the KPI header derive from the same rows, so every write refreshes both. */
const invalidateDealers = (qc: ReturnType<typeof useQueryClient>) => {
  qc.invalidateQueries({ queryKey: ['dealer-investments'] });
  qc.invalidateQueries({ queryKey: queryKeys.dealerDashboard });
};

export const useCreateDealerInvestment = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: Record<string, unknown>) =>
      (await api.post<DealerInvestment>('/dealer-investments', body)).data,
    onSuccess: () => invalidateDealers(qc),
  });
};

export const useUpdateDealerInvestment = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, body }: { id: number; body: Record<string, unknown> }) =>
      (await api.put<DealerInvestment>(`/dealer-investments/${id}`, body)).data,
    onSuccess: () => invalidateDealers(qc),
  });
};

export const useSettleDealerInvestment = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, body }: { id: number; body: Record<string, unknown> }) =>
      (await api.post<DealerInvestment>(`/dealer-investments/${id}/settle`, body)).data,
    onSuccess: () => invalidateDealers(qc),
  });
};

export const useCancelDealerInvestment = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => api.post(`/dealer-investments/${id}/cancel`),
    onSuccess: () => invalidateDealers(qc),
  });
};

export const useDeleteDealerInvestment = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => api.delete(`/dealer-investments/${id}`),
    onSuccess: () => invalidateDealers(qc),
  });
};

// ---------- dealers (accrual model) ----------

const dealerKeys = {
  list: (search?: string, status?: string) => ['dealers', search, status] as const,
  one: (id: number) => ['dealer', id] as const,
  placements: (id: number) => ['dealer-placements', id] as const,
  returns: (id: number) => ['dealer-returns', id] as const,
  receipts: (id: number) => ['dealer-receipts', id] as const,
  ledger: (id: number) => ['dealer-ledger', id] as const,
};

/**
 * Every write touches the same underlying rows, so one helper refreshes the lot rather than each
 * mutation trying to remember which of the six views it happens to affect.
 */
const invalidateDealer = (qc: ReturnType<typeof useQueryClient>, id: number) => {
  qc.invalidateQueries({ queryKey: ['dealers'] });
  qc.invalidateQueries({ queryKey: ['dealer-dashboard-summary'] });
  qc.invalidateQueries({ queryKey: ['dealer-account-dashboard', id] });
  qc.invalidateQueries({ queryKey: dealerKeys.one(id) });
  qc.invalidateQueries({ queryKey: dealerKeys.placements(id) });
  qc.invalidateQueries({ queryKey: dealerKeys.returns(id) });
  qc.invalidateQueries({ queryKey: dealerKeys.receipts(id) });
  qc.invalidateQueries({ queryKey: dealerKeys.ledger(id) });
};

export const useDealerDashboardSummary = () =>
  useQuery({
    queryKey: ['dealer-dashboard-summary'],
    queryFn: () => get<DealerDashboardSummary>('/dealers/dashboard'),
  });

/** One dealer's own dashboard. The API rejects a mismatched id, so this is safe with the token's own dealerId. */
export const useDealerAccountDashboard = (dealerId: number) =>
  useQuery({
    queryKey: ['dealer-account-dashboard', dealerId],
    queryFn: () => get<DealerAccountDashboard>(`/dealers/${dealerId}/dashboard`),
    enabled: dealerId > 0,
  });

export const useDealers = (search?: string, status?: string) =>
  useQuery({
    queryKey: dealerKeys.list(search, status),
    queryFn: () => get<DealerListItem[]>('/dealers', { search, status }),
  });

export const useDealer = (id: number) =>
  useQuery({
    queryKey: dealerKeys.one(id),
    queryFn: () => get<Dealer>(`/dealers/${id}`),
    enabled: id > 0,
  });

export const useDealerPlacements = (id: number) =>
  useQuery({
    queryKey: dealerKeys.placements(id),
    queryFn: () => get<DealerPlacement[]>(`/dealers/${id}/placements`),
    enabled: id > 0,
  });

export const useDealerReturns = (id: number) =>
  useQuery({
    queryKey: dealerKeys.returns(id),
    queryFn: () => get<DealerPrincipalReturn[]>(`/dealers/${id}/returns`),
    enabled: id > 0,
  });

export const useDealerReceipts = (id: number) =>
  useQuery({
    queryKey: dealerKeys.receipts(id),
    queryFn: () => get<DealerProfitReceipt[]>(`/dealers/${id}/receipts`),
    enabled: id > 0,
  });

export const useDealerLedger = (id: number) =>
  useQuery({
    queryKey: dealerKeys.ledger(id),
    queryFn: () => get<DealerLedger>(`/dealers/${id}/ledger`),
    enabled: id > 0,
  });

export const useCreateDealer = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: Record<string, unknown>) =>
      (await api.post<CreateDealerResponse>('/dealers', body)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['dealers'] }),
  });
};

export const useUpdateDealer = (id: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: Record<string, unknown>) => (await api.put<Dealer>(`/dealers/${id}`, body)).data,
    onSuccess: () => invalidateDealer(qc, id),
  });
};

export const useUpdateDealerStatus = (id: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (status: string) => api.patch(`/dealers/${id}/status`, { status }),
    onSuccess: () => invalidateDealer(qc, id),
  });
};

/**
 * Re-issues the dealer's portal password. Nothing is cached: the password exists only in this
 * response, mirroring the investor equivalent.
 */
export const useResetDealerPassword = (id: number) =>
  useMutation({
    mutationFn: async () => (await api.post<DealerCredentials>(`/dealers/${id}/reset-password`)).data,
  });

export const useDeleteDealer = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => api.delete(`/dealers/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['dealers'] }),
  });
};

/**
 * Sent as multipart so the proof-of-payment image travels with the investment itself — a separate
 * upload call could fail and leave a recorded investment with no proof.
 */
export const useAddDealerPlacement = (id: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      amount: number;
      profitRate: number;
      placementDate: string;
      expectedReturnDate?: string | null;
      notes?: string;
      paymentMethod?: string;
      transactionImage?: File | null;
    }) => {
      const form = new FormData();
      form.append('amount', String(input.amount));
      form.append('profitRate', String(input.profitRate));
      form.append('placementDate', input.placementDate);
      if (input.expectedReturnDate) form.append('expectedReturnDate', input.expectedReturnDate);
      if (input.notes) form.append('notes', input.notes);
      if (input.paymentMethod) form.append('paymentMethod', input.paymentMethod);
      if (input.transactionImage) form.append('transactionImage', input.transactionImage);

      const { data } = await api.post<DealerPlacement>(`/dealers/${id}/placements`, form, {
        headers: { 'Content-Type': undefined },
      });
      return data;
    },
    onSuccess: () => invalidateDealer(qc, id),
  });
};

/** Fetches an investment tranche's proof image as an object URL; caller must revoke it. */
export async function fetchDealerPlacementImageUrl(dealerId: number, placementId: number): Promise<string> {
  const response = await api.get(`/dealers/${dealerId}/placements/${placementId}/transaction-image`, {
    responseType: 'blob',
  });
  return URL.createObjectURL(response.data as Blob);
}

/**
 * Sent as multipart so the proof-of-transaction image travels with the withdrawal itself — a
 * separate upload call could fail and leave a recorded return with no proof.
 */
export const useAddDealerReturn = (id: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      placementId: number;
      amount: number;
      returnDate: string;
      reason?: string;
      paymentMethod?: string;
      transactionImage?: File | null;
    }) => {
      const form = new FormData();
      form.append('placementId', String(input.placementId));
      form.append('amount', String(input.amount));
      form.append('returnDate', input.returnDate);
      if (input.reason) form.append('reason', input.reason);
      if (input.paymentMethod) form.append('paymentMethod', input.paymentMethod);
      if (input.transactionImage) form.append('transactionImage', input.transactionImage);

      const { data } = await api.post<DealerPrincipalReturn>(`/dealers/${id}/returns`, form, {
        headers: { 'Content-Type': undefined },
      });
      return data;
    },
    onSuccess: () => invalidateDealer(qc, id),
  });
};

/** Same reasoning as useAddDealerReturn — the proof image travels with the receipt itself. */
export const useAddDealerReceipt = (id: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      amount: number;
      receiptDate: string;
      paymentMethod?: string;
      notes?: string;
      transactionImage?: File | null;
    }) => {
      const form = new FormData();
      form.append('amount', String(input.amount));
      form.append('receiptDate', input.receiptDate);
      if (input.paymentMethod) form.append('paymentMethod', input.paymentMethod);
      if (input.notes) form.append('notes', input.notes);
      if (input.transactionImage) form.append('transactionImage', input.transactionImage);

      const { data } = await api.post<DealerProfitReceipt>(`/dealers/${id}/receipts`, form, {
        headers: { 'Content-Type': undefined },
      });
      return data;
    },
    onSuccess: () => invalidateDealer(qc, id),
  });
};

/** Fetches a return's proof image as an object URL; caller must revoke it. */
export async function fetchDealerReturnImageUrl(dealerId: number, returnId: number): Promise<string> {
  const response = await api.get(`/dealers/${dealerId}/returns/${returnId}/transaction-image`, {
    responseType: 'blob',
  });
  return URL.createObjectURL(response.data as Blob);
}

/** Fetches a profit receipt's proof image as an object URL; caller must revoke it. */
export async function fetchDealerReceiptImageUrl(dealerId: number, receiptId: number): Promise<string> {
  const response = await api.get(`/dealers/${dealerId}/receipts/${receiptId}/transaction-image`, {
    responseType: 'blob',
  });
  return URL.createObjectURL(response.data as Blob);
}

export const useUploadDealerDocument = (dealerId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ type, file }: { type: DocumentType; file: File }) => {
      const form = new FormData();
      form.append('file', file);
      // Let the browser set the multipart boundary — overriding Content-Type breaks the upload.
      const { data } = await api.post(`/dealers/${dealerId}/documents/${type}`, form, {
        headers: { 'Content-Type': undefined },
      });
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: dealerKeys.one(dealerId) }),
  });
};

/** Fetches a dealer document as an object URL; the caller must revoke it. */
export async function fetchDealerDocumentUrl(dealerId: number, type: DocumentType): Promise<string> {
  const response = await api.get(`/dealers/${dealerId}/documents/${type}`, { responseType: 'blob' });
  return URL.createObjectURL(response.data as Blob);
}
